import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { pluck } from 'rxjs/operators';
import { ProjectService } from 'src/app/core/services/project.service';
import { LoadingSpaceType } from 'src/app/core/types';
import { maxErrorKey } from '../../../../shared/validators/validators';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gl-loading-space-gravity-center',
    templateUrl: './loading-space-gravity-center.component.html',
    styleUrls: ['./loading-space-gravity-center.component.scss'],
    standalone: false
})
export class LoadingSpaceGravityCenterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  @Input() public isEdit = false;
  @Output() public submitEvent = new EventEmitter();
  @Input() public loadingSpace: LoadingSpaceType;
  @ViewChild('gravityValue', { static: true }) public gravityValue: ElementRef<HTMLElement>;
  @ViewChild('f', { static: false }) public formEl: FormGroupDirective;
  public setActive: boolean;
  public isSwitchSet = false;
  public form = this.generateForm();
  private pattern = '^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$';
  private subscription: Subscription;

  constructor(private projectService: ProjectService) {}

  public ngOnInit(): void {
    this.setActive = this.loadingSpace?.set;
    this.updateForm(this.loadingSpace);
    if (this.isEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }
    this.projectService.project.pipe(pluck('options')).subscribe(() => {
      this.addMaxValidators();
    });

    this.subscription = this.form.valueChanges.subscribe(() => this.submit());
    this.submitEvent.emit(this.form.value);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.isEdit && changes.isEdit.currentValue) {
      this.isEdit = true;
      this.form.enable();
    } else if (changes.isEdit && !changes.isEdit.currentValue) {
      this.isEdit = false;
      this.form.disable();
    }

    if (changes.chosenLoadingSpace && changes.chosenLoadingSpace.currentValue) {
      this.switchSet(false);
      this.setActive = changes.chosenLoadingSpace.currentValue.set as boolean;
      this.updateForm(changes.chosenLoadingSpace.currentValue);
    }
    this.addMaxValidators();
  }

  private updateForm(loadingSpace: LoadingSpaceType): void {
    this.form.patchValue({
      gravityTrailerLengthTolerance: loadingSpace.gravityTrailerLengthTolerance
        ? loadingSpace.gravityTrailerLengthTolerance
        : null,
      gravitySemiTrailerLengthTolerance: loadingSpace.gravitySemiTrailerLengthTolerance
        ? loadingSpace.gravitySemiTrailerLengthTolerance
        : null,
      gravityShowCenter: loadingSpace.gravityShowCenter ? loadingSpace.gravityShowCenter : null,
      gravityTrailerWidthTolerance: loadingSpace.gravityTrailerWidthTolerance
        ? loadingSpace.gravityTrailerWidthTolerance
        : null,
      gravitySemiTrailerWidthTolerance: loadingSpace.gravitySemiTrailerWidthTolerance
        ? loadingSpace.gravitySemiTrailerWidthTolerance
        : null,
      trailerContainerWeight: loadingSpace.trailerContainerWeight
        ? loadingSpace.trailerContainerWeight
        : null,
      semiTrailerContainerWeight: loadingSpace.semiTrailerContainerWeight
        ? loadingSpace.semiTrailerContainerWeight
        : null,
    });
  }

  public switchSet(set: boolean) {
    this.isSwitchSet = set;
  }

  public submit() {
    if (this.form.get('gravityShowCenter').value) {
      this.loadingSpace = {
        ...this.loadingSpace,
        gravityShowCenter: true,
        gravityTrailerLengthTolerance: this.form.value.gravityTrailerLengthTolerance === null ? 50 : this.form.value.gravityTrailerLengthTolerance,
        gravityTrailerWidthTolerance: this.form.value.gravityTrailerWidthTolerance === null ? 50 : this.form.value.gravityTrailerWidthTolerance,
        gravitySemiTrailerLengthTolerance: this.form.value.gravitySemiTrailerLengthTolerance === null ? 50 : this.form.value.gravitySemiTrailerLengthTolerance,
        gravitySemiTrailerWidthTolerance: this.form.value.gravitySemiTrailerWidthTolerance === null ? 50 : this.form.value.gravitySemiTrailerWidthTolerance,
      };
    }
    if (
      (this.form.dirty &&
        (this.form.controls.gravityTrailerLengthTolerance.dirty ||
          this.form.controls.gravityTrailerWidthTolerance.dirty ||
          this.form.controls.trailerContainerWeight.dirty ||
          this.form.controls.gravitySemiTrailerLengthTolerance.dirty ||
          this.form.controls.gravitySemiTrailerWidthTolerance.dirty ||
          this.form.controls.semiTrailerContainerWeight.dirty) &&
        !this.setActive &&
        this.form.get('trailerContainerWeight').valid &&
        this.form.get('gravityTrailerLengthTolerance').valid &&
        this.form.get('gravityTrailerWidthTolerance').valid) ||
      (this.setActive && this.form.valid)
    ) {
      this.submitEvent.emit(this.form.value);
    } else if (!this.form.get('gravityShowCenter').value) {
      this.submitEvent.emit({
        gravityShowCenter: false,
        gravityTrailerLengthTolerance: null,
        gravitySemiTrailerLengthTolerance: null,
        gravityTrailerWidthTolerance: null,
        trailerContainerWeight: null,
        gravitySemiTrailerWidthTolerance: null,
        semiTrailerContainerWeight: null,
      });
    }
  }

  public delete() {
    this.form.reset();
    this.submitEvent.emit({
      gravityShowCenter: false,
      gravityTrailerLengthTolerance: null,
      gravitySemiTrailerLengthTolerance: null,
      gravityTrailerWidthTolerance: null,
      trailerContainerWeight: null,
      gravitySemiTrailerWidthTolerance: null,
      semiTrailerContainerWeight: null,
    });
  }

  private setControlValidator(name: string, max: number, min = 0): void {
    this.form.controls[name].clearValidators();
    this.form
      .get(name)
      .setValidators([
        Validators.min(min),
        maxErrorKey(max, 'gravityMax'),
        Validators.pattern(this.pattern),
        Validators.required,
      ]);
    this.form.controls[name].updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private addMaxValidators(): void {
    if (!this.loadingSpace) {
      return;
    }
    if (this.loadingSpace.width && this.loadingSpace.height && this.loadingSpace.length) {
      const isAirContainerSingle = this.loadingSpace.type === 4;
      const maxWidth = this.loadingSpace.width / 2;
      const maxLength = isAirContainerSingle
        ? this.loadingSpace.airBaseB / 2
        : this.loadingSpace.length / 2;
      this.setControlValidator('gravityTrailerWidthTolerance', maxWidth, 1);
      this.setControlValidator('gravityTrailerLengthTolerance', maxLength, 1);
      this.form.controls['trailerContainerWeight'].clearValidators();
      this.form
        .get('trailerContainerWeight')
        .setValidators([Validators.min(0), Validators.pattern(this.pattern), Validators.required]);
      this.form.controls['trailerContainerWeight'].updateValueAndValidity({
        onlySelf: true,
        emitEvent: false,
      });
    }
    if (
      this.loadingSpace.set &&
      this.loadingSpace.width1 &&
      this.loadingSpace.height1 &&
      this.loadingSpace.length1
    ) {
      const maxWidth2 = this.loadingSpace.width1 / 2;
      const maxLength2 = this.loadingSpace.length1 / 2;
      this.setControlValidator('gravitySemiTrailerWidthTolerance', maxWidth2, 1);
      this.setControlValidator('gravitySemiTrailerLengthTolerance', maxLength2, 1);
      this.form.controls['semiTrailerContainerWeight'].clearValidators();
      this.form
        .get('semiTrailerContainerWeight')
        .setValidators([Validators.min(0), Validators.pattern(this.pattern), Validators.required]);
      this.form.controls['semiTrailerContainerWeight'].updateValueAndValidity({
        onlySelf: true,
        emitEvent: false,
      });
    }
  }

  private generateForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      gravityShowCenter: new UntypedFormControl(true),
      gravityTrailerLengthTolerance: new UntypedFormControl(null),
      gravitySemiTrailerLengthTolerance: new UntypedFormControl(null),
      gravityTrailerWidthTolerance: new UntypedFormControl(null),
      trailerContainerWeight: new UntypedFormControl(null),
      gravitySemiTrailerWidthTolerance: new UntypedFormControl(null),
      semiTrailerContainerWeight: new UntypedFormControl(null),
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
